import React, { useEffect, useMemo, useState, useRef } from "react";
import Select from "react-select";
import axios from "axios";
import { selectUser } from "../../features/userSlice";
import styles from "./UnloadingEntry.module.css";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { usePopup } from "../../utils/PopupContext";
import dayjs from "dayjs";
import FloatingActions from "../../components/FloatingActions";

const FixUnloadLocationHODv1 = () => {

  const user = useSelector(selectUser);
  const emp_id = user?.emp_id;
  const token = user?.token;
  // const entityCode = user?.entity_code || "SE";
  const topRef = useRef(null);
  const bottomRef = useRef(null);

  const navigate = useNavigate();
  const { showPopup } = usePopup();

  const authHdr = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  /* ================= STATE ================= */

  const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [truckList, setTruckList] = useState([]);
  const [selectedTruck, setSelectedTruck] = useState(null);

  const [placeOptions, setPlaceOptions] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [existingLocationCode, setExistingLocationCode] = useState(null);

  const [unloadingMethod, setUnloadingMethod] = useState("Both");
  const [remarks, setRemarks] = useState("");

  const [qcRequired, setQcRequired] = useState("N");
  const [qcParams, setQcParams] = useState([]);
  const [qcDedAmt, setQcDedAmt] = useState("");

  const [existing, setExisting] = useState({ before_url: null, after_url: null });
  const [txnSnapshot, setTxnSnapshot] = useState(null);

  const [yardPlaceOptions, setYardPlaceOptions] = useState([]);
  const [yardTransferPlace, setYardTransferPlace] = useState(null);
  const [qcTestStatus, setQcTestStatus] = useState('P');


  const qcStatusSelectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: 44,
        borderRadius: 10,
        borderColor: state.isFocused ? '#38bdf8' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(14,165,233,0.15)' : 'none',
        width: '100%',
      }),
      container: (base) => ({
        ...base,
        width: '100%',
        maxWidth: '100%',
      }),
      menu: (base) => ({
        ...base,
        zIndex: 40,
        width: '100%',
        borderRadius: 10,
        overflow: 'hidden',
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      option: (base, state) => ({
        ...base,
        fontSize: '0.92rem',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        lineHeight: 1.35,
        backgroundColor: state.isSelected
          ? '#0b4f8a'
          : state.isFocused
            ? '#e8f3fb'
            : '#fff',
        color: state.isSelected ? '#fff' : '#0f172a',
      }),
      singleValue: (base) => ({
        ...base,
        maxWidth: '100%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }),
    }),
    []
  );

  const qcStatusOptions = useMemo(
    () => [
      { value: 'P', label: 'Pending' },
      { value: 'A', label: 'Accepted' },
      { value: 'D', label: 'Accepted with deduction' },
      { value: 'R', label: 'Rejected' },
    ],
    []
  );

  /* ================= HELPERS ================= */

  const normalizeRows = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.rows)) return data.rows;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  };

  const resetForm = () => {
    setSelectedPlace(null);
    setExistingLocationCode(null);
    setUnloadingMethod("Both");
    setRemarks("");
    setQcRequired("N");
    setQcParams([]);
    setQcDedAmt("");
    setExisting({ before_url: null, after_url: null });
    setTxnSnapshot(null);
    setQcTestStatus('0');
  };

  /* ================= LOAD TRUCKS ================= */

  const loadTrucksByDate = async () => {
    try {
      const res = await axios.post(
        "/api/wbauto/listTrucksByDateAndLocFromPur",
        { date: selectedDate, emp_id },
        authHdr
      );

      const rows = normalizeRows(res.data);

      setTruckList(rows);
      setSelectedTruck(null);
      resetForm();

    } catch (err) {
      showPopup({
        variant: "error",
        title: "Error",
        message: "Failed to load vehicles"
      });
    }
  };

  /* ================= LOAD YARD PLACES ================= */

  const fetchUnloadingYardPlaces = async () => {
    const res = await axios.post('/api/wbauto/getUnloadingStations');
    const data = res.data || [];
    const formatted = data
      .map(row => ({
        value: row.L_PlaceId,
        label: row.L_PlaceName,
        entity_code: row.entity_code
      }));
    setYardPlaceOptions(formatted);
  };

  /* ================= LOAD STATIONS ================= */

  useEffect(() => {
    const fetchStations = async () => {
      try {
        const res = await axios.post(
          "/api/wbauto/getUsersUnloadingStations",
          { emp_id, token },
          authHdr
        );

        const rows = normalizeRows(res.data);

        setPlaceOptions(
          rows.map(r => ({
            value: r.L_PlaceId,
            label: r.L_PlaceName
          }))
        );

      } catch (e) {
        console.error("Station fetch failed");
      }
    };

    fetchStations();
    fetchUnloadingYardPlaces()
  }, []);

  /* ================= AUTO APPLY EXISTING LOCATION ================= */

  useEffect(() => {
    if (existingLocationCode && placeOptions.length > 0) {

      const found = placeOptions.find(
        p => String(p.value) === String(existingLocationCode)
      );

      if (found) {
        setSelectedPlace(found);
      }
    }
  }, [existingLocationCode, placeOptions]);

  /* ================= FETCH EXISTING TXN ================= */

  const fetchExistingTxn = async (truck) => {
    try {
      const docnumber = truck?.p_docnumber || truck?.docnumber;
      if (!docnumber) return;

      const res = await axios.post(
        "/api/wbauto/getUnloadTxn",
        { docnumber, entity_code: truck.entity_code, token },
        authHdr
      );

      const raw = res.data?.rows || res.data?.data || res.data || null;
      const d = Array.isArray(raw) ? raw[0] : raw;

      console.log("Raw D", d);

      if (!d || d.found === false) {
        resetForm();
        return;
      }

      setTxnSnapshot(d);

      setExisting({
        before_url: d.before_url || null,
        after_url: d.after_url || null
      });

      setUnloadingMethod(d.unloading_method || "Both");
      setQcRequired((d.qc_required || "N").toUpperCase());
      setRemarks(d.remarks || "");
      setQcDedAmt(d.qc_ded_amt ?? "");
      setQcTestStatus(
        (() => {
          const raw = String(d.qc_test_status ?? '').trim().toUpperCase();
          // New codes
          if (['P', 'A', 'D', 'R'].includes(raw)) return raw;
          // Legacy numeric → letter (same sequence)
          if (raw === '0') return 'P';
          if (raw === '1') return 'A';
          if (raw === '2') return 'D';
          if (raw === '3') return 'R';
          return 'P';
        })()
      );

      if (d.location_code) {

        const matched = placeOptions.find(
          p => String(p.value) === String(d.location_code)
        );

        if (matched) {
          setSelectedPlace(matched);
        } else {
          // fallback if options not loaded yet
          setSelectedPlace({
            value: d.location_code,
            label: d.location_name || `Location ${d.location_code}`
          });
        }
      }

    } catch (e) {
      console.error("Txn fetch failed");
      setQcTestStatus('0');
    }
  };

  /* ================= FETCH QC ================= */

  const fetchQcParams = async (truck) => {
    try {
      const docnumber = truck?.p_docnumber || truck?.docnumber;

      const res = await axios.post(
        "/api/wbauto/getQcParamsForTruck",
        { entity_code: truck.entity_code, docnumber, token },
        authHdr
      );

      const rows = normalizeRows(res.data);

      if (rows.length > 0) {
        setQcParams(rows.map(p => ({
          test_code: p.test_code,
          test_desc: p.test_desc,
          value: p.test_value ?? ""
        })));
        return;
      }

      // fallback from txn table
      if (txnSnapshot?.qc_test_code) {
        setQcParams([{
          test_code: txnSnapshot.qc_test_code,
          test_desc: txnSnapshot.qc_test_code,
          value: txnSnapshot.qc_test_value ?? ""
        }]);
      }

    } catch (e) {
      console.error("QC fetch failed");
      setQcParams([]);
    }
  };

  /* ================= WHEN TRUCK CHANGES ================= */

  useEffect(() => {
    if (!selectedTruck) return;

    resetForm();

    fetchExistingTxn(selectedTruck);
    fetchQcParams(selectedTruck);

  }, [selectedTruck]);

  /* ================= QC CHANGE ================= */

  const handleQcChange = (index, value) => {
    setQcParams(prev => {
      const updated = [...prev];
      updated[index].value = value;
      return updated;
    });
  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async () => {

    if (!selectedPlace) {
      showPopup({ variant: "warning", title: "Location", message: "Select location" });
      return;
    }

    const docnumber = selectedTruck.p_docnumber || selectedTruck.docnumber;
    const truck_no = selectedTruck.P_truck || selectedTruck.truck_no;

    const fd = new FormData();

    fd.append("entity_code", selectedTruck.entity_code);
    fd.append("docnumber", docnumber);
    fd.append("truck_no", truck_no);
    fd.append("locationCode", selectedPlace.value);
    fd.append("locationName", selectedPlace.label);
    fd.append("unloading_method", unloadingMethod);
    fd.append("qc_required", qcRequired);
    fd.append("remarks", remarks);
    fd.append("qc_ded_amt", qcDedAmt);
    fd.append("emp_id", emp_id);
    fd.append("token", token);
    fd.append('qc_test_status', qcTestStatus ?? 'P');
    fd.append("transfer_location_name", yardTransferPlace?.label ?? "");
    fd.append("transfer_location_code", yardTransferPlace?.value ?? "");

    fd.append("qc_details", JSON.stringify(
      qcParams.map(p => ({
        test_code: p.test_code,
        test_value: p.value
      }))
    ));

    try {
      const res = await axios.post(
        "/api/wbauto/updateCorrectionUnloadingEntry",
        fd,
        { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` } }
      );

      if (res.data?.success) {
        showPopup({ variant: "success", title: "Updated", message: "Correction Saved" });
        loadTrucksByDate();
      }
      else {
        showPopup({ variant: "error", title: "Update Failed", message: res.data?.message || "Something went wrong" });
      }
    }
    catch (error) {
      showPopup({ variant: "error", title: "Update Failed", message: error.response?.data?.message || "Something went wrong" });
    }
  };

  /* ================= UI ================= */
  if (!token) {
    return (
      <div>
        <p>You must be logged in to register a employee.</p>
        <button onClick={() => navigate("/")}>Go to Login</button>
      </div>
    );
  }

  return (
    <div className={styles.approveContainer}>
      <div ref={topRef} />

      <h2 className={styles.screenTitle}>Unloading Correction (HoD)</h2>

      <div className={styles.filters}>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
        <button onClick={loadTrucksByDate}>Load</button>
      </div>

      <div className={styles.filters}>
        <Select
          placeholder="Select Truck"
          options={truckList.map(t => ({
            value: t.p_docnumber || t.docnumber,
            label: `${t.P_truck || t.truck_no} - ${t.p_item_name || t.item_name}`,
            data: t
          }))}
          onChange={(opt) => setSelectedTruck(opt?.data)}
        />
      </div>

      {selectedTruck && (
        <div className="card p-3 mt-3">

          <h5>📸 Existing Photos</h5>
          <div className={styles.photoGrid}>
            {existing.before_url && (
              <img className={styles.photoThumb} src={existing.before_url} alt="Before" />
            )}
            {existing.after_url && (
              <img className={styles.photoThumb} src={existing.after_url} alt="After" />
            )}
          </div>

          <label>Unloading Location</label>
          <Select
            options={placeOptions}
            value={selectedPlace}
            onChange={(val) => {
              setSelectedPlace(val);
              setYardTransferPlace(null);
            }}
          />

          <label>Unloading Method</label>
          <Select
            value={{ value: unloadingMethod, label: unloadingMethod }}
            options={["Labour", "Machine", "Both", "Self"].map(v => ({ value: v, label: v }))}
            onChange={(opt) => setUnloadingMethod(opt.value)}
          />

          <label>QC Required?</label>
          <div className={styles.unloadingMethods}>
            {["Y", "N"].map(v => (
              <label key={v}>
                <input
                  type="radio"
                  value={v}
                  checked={qcRequired === v}
                  onChange={(e) => setQcRequired(e.target.value)}
                />
                {v === "Y" ? "Yes" : "No"}
              </label>
            ))}
          </div>

          <h5 className={styles.qcTitle}>🧪 QC Parameters</h5>
          {qcParams.map((p, i) => (
            <div key={p.test_code} className={styles.qcCard}>
              <div>{p.test_code} - {p.test_desc}</div>
              <input
                type="number"
                className={styles.qcInput}
                value={p.value}
                onChange={(e) => handleQcChange(i, e.target.value)}
              />
            </div>
          ))}

          <h5>🧪 QC Test Status</h5>
          <div className={styles.qcStatusWrap}>
            <Select
              className={styles.select}
              classNamePrefix="qcStatus"
              styles={qcStatusSelectStyles}
              isSearchable={false}
              options={qcStatusOptions}
              value={qcStatusOptions.find((o) => o.value === String(qcTestStatus)) || qcStatusOptions[0]}
              onChange={(opt) => setQcTestStatus(opt?.value ?? 'P')}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
              menuShouldScrollIntoView={false}
            />
          </div>

          <h5>💰 QC Deduction</h5>
          <input
            disabled={qcTestStatus !== 'D'}
            type="number"
            className="form-control"
            value={qcDedAmt}
            onChange={(e) => setQcDedAmt(e.target.value)}
          />

          <div style={{ marginTop: 7, marginBottom: 7 }}>
            <h5>Yard Transfer To Place:</h5>

            <Select
              options={yardPlaceOptions.filter(
                place => place.value !== selectedPlace?.value
              )}
              value={yardTransferPlace}
              onChange={(val) => {
                setYardTransferPlace(val);
              }}
              className={styles.select}
            />
          </div>

          <label>Remarks</label>
          <textarea
            className="form-control"
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />

          <button className={styles.approveBtn} onClick={handleSubmit}>
            Submit Correction
          </button>

        </div>
      )}

      <button className={styles.cancelBtn} onClick={() => navigate("/dashboard")}>
        Cancel
      </button>

      <FloatingActions topRef={topRef} bottomRef={bottomRef} />
      <div ref={bottomRef} />
    </div>
  );
};

export default FixUnloadLocationHODv1;